import type { FetcherOptions, up } from 'up-fetch'

/** The Organisation requests are limited to, or all organisations. */
export type OrganisationScope = { id: number; kind: 'organisation' } | { kind: 'all' }

/** The scope's Organisation, or undefined for "All organisations". */
export const getScopeOrganisationId = (scope: OrganisationScope) =>
  scope.kind === 'organisation' ? scope.id : undefined

export type UpFetch = UpFetchCall & {
  // What a query key appends to name the scope's Organisation; `@repo/api` procedures read it.
  getQueryKeyContext: () => QueryKeyContext
  // The upFetch a scope wraps, so scoping again replaces the scope instead of nesting it.
  unscoped: UpFetchCall
}

type FetcherHeaders = FetcherOptions<typeof fetch, never, unknown, unknown>['headers']
type QueryKeyContext = readonly unknown[]
type UpFetchCall = ReturnType<typeof up<typeof fetch>>

const ORGANISATION_HEADER = 'X-Organisation-Id'

const headerEntries = (headers: FetcherHeaders) =>
  headers instanceof Headers || Array.isArray(headers)
    ? [...new Headers(headers)]
    : Object.entries(headers ?? {})

const isOrganisationHeader = ([key]: [string, unknown]) =>
  key.toLowerCase() === ORGANISATION_HEADER.toLowerCase()

const organisationKeyContext = (scope: OrganisationScope): QueryKeyContext =>
  scope.kind === 'organisation' ? [{ orgId: scope.id }] : []

/**
 * Limits one upFetch to `scope`: its requests send the scope's Organisation and its query keys
 * name it. The scope replaces any earlier one and wins over an Organisation header on the request.
 */
export const scopeUpFetch = (upFetch: UpFetch | UpFetchCall, scope: OrganisationScope): UpFetch => {
  const unscoped = 'unscoped' in upFetch ? upFetch.unscoped : upFetch
  const scoped: UpFetchCall = (input, options, ctx) =>
    unscoped(
      input,
      {
        ...options,
        headers: {
          ...Object.fromEntries(
            headerEntries(options?.headers).filter(entry => !isOrganisationHeader(entry)),
          ),
          // up-fetch drops an undefined header, so `all` also clears an inherited one.
          [ORGANISATION_HEADER]: getScopeOrganisationId(scope),
        },
      },
      ctx,
    )

  return Object.assign(scoped, {
    getQueryKeyContext: () => organisationKeyContext(scope),
    unscoped,
  })
}
